import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useReducedMotion } from "motion/react"

import { useAppState } from "../../context/appStateContext"
import native from "../../native/native"
import { formatCurrency } from "../../utils/utils"
import styles from "./BeaconPaywall.module.scss"

/**
 * BeaconPaywall (`/beacon`) — a self-contained EXPIRATION / RENEWAL paywall for
 * Private Photo Vault, staged as a HARBOUR NOCTURNE. The user's memories are
 * little boats moored across a dark bay; the vault is the LIGHTHOUSE that keeps
 * a steady watch over them. Renewing keeps the beacon lit through the year.
 *
 * Divergence brief: claude-paywalls/_ledger.md · concept doc: claude-paywalls/beacon.md
 *
 * Mechanic (new to the ledger): HAND-OVER-HAND ROPE HAUL. A halyard hangs down
 * the tower; haul it down in strokes (the handle springs back so you can grab
 * again, hand over hand) and the lamp climbs from a guttering flame to a full
 * beam. As the beam strengthens it brings the bay under watch outward from the
 * lighthouse — each boat gains a steady "watched" halo (the photos stay full
 * colour throughout — the beam is warmth, not a grey→colour reveal). "Light the
 * beacon" and the keyboard (Enter/Space) also raise it; a short auto-demo hauls
 * a couple of strokes then hands the rope over.
 *
 * Every ledger axis is chosen to differ from the 8 prior expiration concepts:
 *   - Layout:      a HARBOUR SEASCAPE / NOCTURNE (lighthouse + a bay of moored
 *                  memory-boats) — not Deep Freeze's hero card, Unbroken's month
 *                  grid, Ahead's road, Keystone's arch, Keep Posted's flap board,
 *                  Patchwork's quilt frame, Evergreen's radial wheel or Keepsake's
 *                  card deck.
 *   - Interaction: a HAND-OVER-HAND HALYARD HAUL (repeated downward strokes that
 *                  accumulate) — distinct from every scrub / hold / flip / toss /
 *                  paint / dial / riffle / node-graph / draw-a-link / lever / stitch /
 *                  lasso / drag-a-piece prior.
 *   - Register:    STEADFAST, WATCHFUL, seafaring vigil — the calm of a kept light.
 *                  Not Deep Freeze cold-fear, Unbroken earned-pride, Ahead hopeful-road,
 *                  Keystone load-bearing, Keep Posted retro-mechanical, Patchwork
 *                  handmade-textile, Evergreen nurturing-seasonal or Keepsake collector.
 *   - Art:        the FIRST NOCTURNE since the early cluster, but deliberately warm &
 *                  PHOTOGRAPHIC (memory-boats), not black skeuomorphic UI — deep teal
 *                  night + moonlit brand-blue water = the WATCHED/safe state, warm amber
 *                  = the lamp / the not-yet-watched boats. Photos are always full colour.
 *   - Value:      CONTINUITY OF AUTOMATIC PROTECTION for RENEWAL via a keeper's-watch
 *                  frame (vigilance + honest loss-aversion) — "your vault keeps a steady
 *                  watch; keep the beacon lit for the year ahead."
 *
 * Honest & App Store compliant: the beacon "keeps watch" — it never holds photos
 * hostage and nothing is ever deleted (copy says so). The coverage date is the real
 * today + term, computed live; Save % is exact yearly-vs-monthly arithmetic; no fake
 * countdown or scarcity. Purchase is never gated on the demo. Yearly is the featured,
 * default plan and carries the most prominent price; monthly is small and secondary.
 * Memory-boats are bundled demo photos from `public/byheart/` (no network egress).
 */

type PlanKey = "yearly" | "monthly"

// the lamp point inside the stage (percent coords) — the beam's origin
const LAMP = { x: 85, y: 27 }

// this year's memories, moored across the bay (bundled demo photos)
const BOATS = [
  { src: "/byheart/m03.jpg", x: 15, y: 61 },
  { src: "/byheart/m07.jpg", x: 62, y: 60 },
  { src: "/byheart/m11.jpg", x: 40, y: 66 },
  { src: "/byheart/m05.jpg", x: 74, y: 71 },
  { src: "/byheart/m02.jpg", x: 25, y: 76 },
  { src: "/byheart/m13.jpg", x: 52, y: 80 },
  { src: "/byheart/m16.jpg", x: 12, y: 86 },
  { src: "/byheart/m09.jpg", x: 36, y: 90 },
  { src: "/byheart/m18.jpg", x: 66, y: 88 },
].map((b, id) => ({
  ...b,
  id,
  dist: Math.hypot(b.x - LAMP.x, (b.y - LAMP.y) * 0.85),
}))
const TOTAL = BOATS.length
// order in which the strengthening beam brings boats under watch (nearest first)
const WATCH_ORDER = [...BOATS].sort((a, b) => a.dist - b.dist).map((b) => b.id)

const HAUL_FULL = 460 // px of downward pull that fully lights the beacon
const clamp = (v: number, lo: number, hi: number) =>
  Math.max(lo, Math.min(hi, v))

// ── tiny inline icons ─────────────────────────────────────────────────────────
const Tick = ({ size = 11 }: { size?: number }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} aria-hidden>
    <path
      d="M5 12.5l4.2 4.2L19 7"
      fill="none"
      stroke="currentColor"
      strokeWidth={3}
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)
const Lock = ({ size = 13 }: { size?: number }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} aria-hidden>
    <rect
      x={4.5}
      y={10.5}
      width={15}
      height={10}
      rx={2.6}
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
    />
    <path
      d="M7.5 10.5V8a4.5 4.5 0 0 1 9 0v2.5"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
    />
  </svg>
)
const Flame = ({ size = 13 }: { size?: number }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} aria-hidden>
    <path
      d="M12 3.5c2.6 3 4.6 4.7 4.6 8.2A4.6 4.6 0 0 1 12 16.3a4.6 4.6 0 0 1-4.6-4.6c0-1.6.7-2.7 1.7-3.8.3 1 .9 1.6 1.7 1.9C10.8 7.7 10.9 5.7 12 3.5z"
      fill="currentColor"
    />
  </svg>
)

const BeaconPaywall = () => {
  const { state } = useAppState()
  const reduceMotion = useReducedMotion()

  // ── pricing (real debug data; yearly featured) ──────────────────────────────
  const premium = state.productData?.premium
  const yearlyProduct = premium?.yearlyProduct
  const monthlyProduct = premium?.monthlyProduct
  const locale = yearlyProduct?.priceLocale

  const yearlyPrice = yearlyProduct
    ? formatCurrency(yearlyProduct.price, locale)
    : "$35.99"
  const yearlyPerMonth = yearlyProduct
    ? formatCurrency(yearlyProduct.price / 12, locale)
    : "$3.00"
  const monthlyPrice = monthlyProduct
    ? formatCurrency(monthlyProduct.price, locale)
    : "$13.99"
  const hasTrial = yearlyProduct?.hasTrial ?? true
  const trialDays = yearlyProduct?.introPricing?.period ?? 7

  const savingsPct = useMemo(() => {
    const y = yearlyProduct?.price ?? 35.99
    const m = monthlyProduct?.price ?? 13.99
    if (!m) return 0
    return Math.round((1 - y / (m * 12)) * 100)
  }, [yearlyProduct, monthlyProduct])

  const [plan, setPlan] = useState<PlanKey>("yearly")

  // the real coverage date if you renew now — today + the plan's term
  const coverThrough = useMemo(() => {
    const d = new Date()
    d.setMonth(d.getMonth() + (plan === "yearly" ? 12 : 1))
    return d.toLocaleDateString("en-US", { month: "short", year: "numeric" })
  }, [plan])

  // ── beacon state ─────────────────────────────────────────────────────────────
  const [charge, setCharge] = useState(0) // 0 (guttering) → 1 (full beam)
  const [pull, setPull] = useState(0) // live handle offset while hauling
  const [handed, setHanded] = useState(false) // auto-demo done / user has the rope

  const draggingRef = useRef(false)
  const lastYRef = useRef(0)
  const hauledRef = useRef(0)
  const springRef = useRef<number | null>(null)
  const rampRef = useRef<number | null>(null)
  const demoRef = useRef<number | null>(null)

  const onWatch = Math.round(charge * TOTAL)
  const watched = useMemo(() => {
    const s = new Set<number>()
    for (let i = 0; i < onWatch; i++) s.add(WATCH_ORDER[i])
    return s
  }, [onWatch])
  const lit = charge > 0.995

  const stopSpring = () => {
    if (springRef.current) cancelAnimationFrame(springRef.current)
    springRef.current = null
  }
  const stopRamp = () => {
    if (rampRef.current) cancelAnimationFrame(rampRef.current)
    rampRef.current = null
  }
  const stopDemo = () => {
    if (demoRef.current) window.clearTimeout(demoRef.current)
    demoRef.current = null
  }

  // spring the rope handle back to rest so it can be hauled again (hand over hand)
  const springBack = useCallback(() => {
    stopSpring()
    const step = () => {
      setPull((p) => {
        const next = p * 0.72
        if (next < 0.6) {
          springRef.current = null
          return 0
        }
        springRef.current = requestAnimationFrame(step)
        return next
      })
    }
    springRef.current = requestAnimationFrame(step)
  }, [])

  // smoothly ramp the charge to a target (used by demo + "Light the beacon")
  const rampTo = useCallback((target: number, ms: number) => {
    stopRamp()
    let start: number | null = null
    let from = 0
    setCharge((c) => {
      from = c
      return c
    })
    const tick = (t: number) => {
      if (start === null) start = t
      const k = clamp((t - start) / ms, 0, 1)
      const eased = 1 - Math.pow(1 - k, 3)
      setCharge(from + (target - from) * eased)
      if (k < 1) rampRef.current = requestAnimationFrame(tick)
      else rampRef.current = null
    }
    rampRef.current = requestAnimationFrame(tick)
  }, [])

  // ── the teaching haul: raise it a little, then hand the rope over ────────────
  useEffect(() => {
    if (reduceMotion) {
      setCharge(0.28)
      setHanded(true)
      return
    }
    demoRef.current = window.setTimeout(() => {
      hauledRef.current = HAUL_FULL * 0.34
      rampTo(0.34, 1100)
      setPull(70)
      window.setTimeout(springBack, 520)
      demoRef.current = window.setTimeout(() => setHanded(true), 1250)
    }, 720)
    return () => {
      stopDemo()
      stopRamp()
      stopSpring()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reduceMotion])

  // ── pointer: haul the halyard ────────────────────────────────────────────────
  const beginInteract = () => {
    stopDemo()
    stopRamp()
    setHanded(true)
  }

  const onDown = (e: React.PointerEvent) => {
    beginInteract()
    stopSpring()
    draggingRef.current = true
    lastYRef.current = e.clientY
    ;(e.currentTarget as Element).setPointerCapture?.(e.pointerId)
  }
  const onMove = (e: React.PointerEvent) => {
    if (!draggingRef.current) return
    const dy = e.clientY - lastYRef.current
    lastYRef.current = e.clientY
    if (dy > 0) {
      hauledRef.current += dy
      const c = clamp(hauledRef.current / HAUL_FULL, 0, 1)
      setCharge(c)
      if (c >= 1 && !lit) native.logMixpanelEvent("html_beacon_lit", {})
    }
    setPull((p) => clamp(p + dy, 0, 104))
  }
  const onUp = () => {
    if (!draggingRef.current) return
    draggingRef.current = false
    springBack()
  }

  const lightBeacon = () => {
    beginInteract()
    hauledRef.current = HAUL_FULL
    rampTo(1, 900)
    native.logMixpanelEvent("html_beacon_light_button", {})
  }
  const letRest = () => {
    beginInteract()
    stopRamp()
    hauledRef.current = 0
    setCharge(0)
    native.logMixpanelEvent("html_beacon_reset", {})
  }
  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault()
      lightBeacon()
    }
  }

  // ── plan + purchase wiring ────────────────────────────────────────────────────
  const choosePlan = (key: PlanKey) => {
    setPlan(key)
    native.logMixpanelEvent("html_pricing_toggle", { plan: key })
  }
  const handleClose = () => native.postNativeEvent("close")
  const purchase = () => {
    const product = plan === "yearly" ? yearlyProduct : monthlyProduct
    if (product) native.subscribe(product)
  }

  // ── copy ───────────────────────────────────────────────────────────────────
  const headline = lit
    ? "The whole harbour's on watch."
    : "Keep the light on every memory."
  const sub = lit
    ? "Full-quality originals, private and encrypted, backed up and in sync on every device — kept automatically, all year long."
    : "Your vault has kept a steady watch over every photo you've saved. Light the beacon — and keep it lit for the year ahead."

  const hint = lit
    ? "Beacon lit. This is the watch Premium keeps for you — automatically, every single day."
    : !handed
      ? "Lighting the lamp…"
      : onWatch > 0
        ? `${onWatch} of ${TOTAL} memories back under watch — keep hauling.`
        : "Haul the halyard down, hand over hand, to raise the lamp."

  const ctaLabel =
    plan === "yearly"
      ? hasTrial
        ? `Start ${trialDays} days free`
        : "Keep the beacon lit — Yearly"
      : "Choose monthly"
  const ctaSub =
    plan === "yearly"
      ? hasTrial
        ? `${trialDays} days free, then ${yearlyPrice}/yr · cancel anytime`
        : `${yearlyPrice}/yr · auto-renews · cancel anytime`
      : `${monthlyPrice}/mo · auto-renews · cancel anytime`

  const stageVars = {
    ["--charge" as any]: charge.toFixed(3),
    ["--pull" as any]: `${pull.toFixed(1)}px`,
  } as React.CSSProperties

  return (
    <div className={styles.page}>
      <button className={styles.close} onClick={handleClose} aria-label="Close">
        <svg viewBox="0 0 24 24" width={16} height={16} aria-hidden>
          <path
            d="M6 6l12 12M18 6L6 18"
            stroke="currentColor"
            strokeWidth={2.2}
            strokeLinecap="round"
          />
        </svg>
      </button>

      <header className={styles.head}>
        <div className={styles.brand}>
          <span className={styles.brandKey} aria-hidden>
            <Lock size={12} />
          </span>
          <span>Private Photo Vault</span>
          <span className={styles.premiumPip}>Premium</span>
        </div>
        <h1 className={`${styles.title} ${lit ? styles.titleLit : ""}`}>
          {headline}
        </h1>
        <p className={styles.sub}>{sub}</p>
      </header>

      {/* ── the harbour nocturne ──────────────────────────────────────────────── */}
      <div
        className={`${styles.stage} ${lit ? styles.stageLit : ""} ${
          reduceMotion ? styles.stageStill : ""
        }`}
        style={stageVars}
        role="group"
        tabIndex={0}
        aria-label="Harbour at night — haul the halyard to light the beacon"
        onKeyDown={onKeyDown}
      >
        <span className={styles.stars} aria-hidden />
        <span className={styles.moon} aria-hidden />
        <span className={styles.sea} aria-hidden />
        <span className={styles.moonpath} aria-hidden />

        {/* the sweeping beam + its glow, both scaled by charge */}
        <span className={styles.beamWrap} aria-hidden>
          <span className={styles.beam} />
          <span className={styles.beam2} />
          <span className={styles.lampGlow} />
        </span>

        {/* moored memory-boats */}
        {BOATS.map((b) => {
          const on = watched.has(b.id)
          return (
            <span
              key={b.id}
              className={`${styles.boat} ${on ? styles.boatOn : ""}`}
              style={{ left: `${b.x}%`, top: `${b.y}%` }}
            >
              <span className={styles.halo} aria-hidden />
              <span className={styles.hull}>
                <img
                  src={b.src}
                  alt=""
                  draggable={false}
                  onError={(e) =>
                    ((e.currentTarget as HTMLImageElement).style.opacity = "0")
                  }
                />
              </span>
              <span className={styles.watchPip} aria-hidden>
                <Tick size={9} />
              </span>
            </span>
          )
        })}

        {/* the lighthouse on its headland */}
        <span className={styles.headland} aria-hidden />
        <span className={styles.tower} aria-hidden>
          <span className={styles.towerBody} />
          <span className={styles.gallery} />
          <span className={styles.lantern}>
            <span className={styles.lamp}>
              <Flame size={12} />
            </span>
          </span>
          <span className={styles.cap} />
        </span>

        {/* the halyard you haul, hand over hand */}
        <span
          className={styles.halyard}
          onPointerDown={onDown}
          onPointerMove={onMove}
          onPointerUp={onUp}
          onPointerCancel={onUp}
          role="button"
          aria-label="Haul the halyard"
        >
          <span className={styles.rope} />
          <span className={styles.grip}>
            <span className={styles.gripKnot} />
          </span>
        </span>

        {/* overlays */}
        <span className={styles.watchTag} aria-hidden>
          <span className={styles.watchDot} />
          ON WATCH {onWatch}/{TOTAL}
        </span>
        <span className={styles.coverPlate} aria-hidden>
          <Flame size={11} />
          LIT THROUGH {coverThrough.toUpperCase()}
        </span>
      </div>

      <p
        className={`${styles.hintLine} ${lit ? styles.hintLit : ""}`}
        aria-live="polite"
      >
        {hint}
      </p>

      <div className={styles.tools}>
        <button
          className={styles.toolPrimary}
          onClick={lightBeacon}
          disabled={lit}
        >
          <Flame size={13} />
          {lit ? "Beacon lit" : "Light the beacon"}
        </button>
        <button
          className={styles.toolGhost}
          onClick={letRest}
          disabled={charge <= 0.001}
        >
          Let it rest
        </button>
      </div>

      {/* what the watch actually is — the honest Premium bundle */}
      <ul className={styles.keeps}>
        {[
          "Every original kept in full quality — backed up automatically",
          "Private & encrypted — only your passcode opens the vault",
          "In sync on every device — your whole library, everywhere",
        ].map((t) => (
          <li key={t}>
            <span className={styles.keepDot} aria-hidden>
              <Tick size={10} />
            </span>
            {t}
          </li>
        ))}
      </ul>

      {/* ── plans — yearly featured, monthly small & secondary ─────────────────── */}
      <div className={styles.plans}>
        <button
          className={`${styles.planYear} ${
            plan === "yearly" ? styles.planSel : ""
          }`}
          onClick={() => choosePlan("yearly")}
          aria-pressed={plan === "yearly"}
        >
          <span className={styles.planMain}>
            <span className={styles.planName}>Yearly</span>
            <span className={styles.planNote}>
              Keep the beacon lit all year
            </span>
          </span>
          <span className={styles.planPrice}>
            <span className={styles.planBig}>{yearlyPrice}</span>
            <span className={styles.planPer}>/yr</span>
            <span className={styles.planSmall}>
              {yearlyPerMonth}/mo
              {savingsPct > 0 ? ` · save ${savingsPct}%` : ""}
            </span>
          </span>
          {savingsPct > 0 && (
            <span className={styles.saveBadge}>SAVE {savingsPct}%</span>
          )}
        </button>

        <button
          className={`${styles.planMonth} ${
            plan === "monthly" ? styles.planMonthSel : ""
          }`}
          onClick={() => choosePlan("monthly")}
          aria-pressed={plan === "monthly"}
        >
          <span className={styles.planMonthName}>Monthly</span>
          <span className={styles.planMonthPrice}>{monthlyPrice}/mo</span>
        </button>
      </div>

      <button className={styles.cta} onClick={purchase}>
        <span className={styles.ctaLabel}>{ctaLabel}</span>
        <span className={styles.ctaSub}>{ctaSub}</span>
      </button>

      <p className={styles.reassure}>
        The beacon only keeps watch — it never holds your memories hostage. Your
        photos are always yours, and nothing is ever deleted.
      </p>

      <div className={styles.legal}>
        <button onClick={() => native.postNativeEvent("terms")}>Terms</button>
        <span aria-hidden>·</span>
        <button onClick={() => native.postNativeEvent("privacy")}>
          Privacy
        </button>
        <span aria-hidden>·</span>
        <button onClick={() => native.postNativeEvent("already_purchased")}>
          Restore
        </button>
      </div>
    </div>
  )
}

export default BeaconPaywall
